/* Fresh Prints Sticker Studio: presentation and informational size guidance. The Studio owns selections,
   pricing, uploads, review and checkout. Never replace its control nodes. */
(() => {
  'use strict';
  const root = document.getElementById('fp-sticker-studio');
  if (!root || root.dataset.motionReady === 'true') return;
  const configNode = root.querySelector('[data-studio-motion-config]');
  let config;
  try { config = JSON.parse(configNode?.textContent || '{}'); } catch { return; }
  if (!config.freshF || !config.references) return;

  const groups = ['material-scroll', 'shape-options', 'size-options']
    .map(id => root.querySelector(`#${id}`)).filter(Boolean);
  if (groups.length !== 3) return;
  const references = { '2': 'AA battery', '3': 'Credit card', '4': 'Floppy disk' };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const running = new WeakMap();
  let frame = 0;
  let observer;
  let smallStickerDialog;
  let smallStickerOpener;

  function openSmallStickerGuide(button) {
    smallStickerOpener = button;
    if (!smallStickerDialog) {
      smallStickerDialog = document.createElement('dialog');
      smallStickerDialog.id = 'fp-one-inch-dialog';
      smallStickerDialog.setAttribute('aria-labelledby', 'fp-one-inch-title');
      smallStickerDialog.setAttribute('aria-describedby', 'fp-one-inch-intro');
      smallStickerDialog.innerHTML = `
        <div class="fp-small-sticker-shell">
          <div class="fp-small-sticker-header"><span>STICKER SIZE GUIDE</span><button type="button" data-small-sticker-close aria-label="Close sticker size guide">×</button></div>
          <h2 id="fp-one-inch-title" tabindex="-1">Need 1″ stickers or smaller?</h2>
          <p id="fp-one-inch-intro">Our smallest individually die-cut sticker is <strong>2″</strong>. Tiny loose stickers are difficult to pop out, stack and handle—they scatter like confetti.</p>
          <section class="fp-small-sticker-recommendation" aria-labelledby="fp-small-sticker-route">
            <span class="fp-small-sticker-kicker">THE BETTER OPTION FOR SMALL STICKERS</span>
            <h3 id="fp-small-sticker-route">A kiss-cut gang sheet</h3>
            <p>Arrange your small logos on a <strong>52″-wide Arlon 510 GTX sheet</strong> and upload the finished layout. Pay by the linear foot. We print and kiss-cut the logos, leaving them together on the backing so you can peel them off as needed.</p>
          </section>
          <div class="fp-small-sticker-file-note"><strong>Print-and-cut file required</strong><p>Supply a production-ready PDF or EPS with vector contour-cut paths, and request kiss cutting in the order notes. This is an upload service, not a sticker builder.</p></div>
          <div class="fp-small-sticker-actions"><a href="/products/arlon-dpf-510gtx-vinyl-gang-sheet" target="_blank" rel="noopener" aria-label="View Arlon 510 GTX (opens a new tab)">View Arlon 510 GTX <span aria-hidden="true">↗</span></a><button type="button" data-small-sticker-close>Keep designing · 2″ and up</button></div>
        </div>`;
      document.body.append(smallStickerDialog);
      smallStickerDialog.addEventListener('click', event => {
        if (event.target.closest('[data-small-sticker-close]')) smallStickerDialog.close();
        if (event.target === smallStickerDialog) {
          const r = smallStickerDialog.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) smallStickerDialog.close();
        }
      });
      smallStickerDialog.addEventListener('close', () => {
        if (smallStickerOpener?.isConnected) smallStickerOpener.focus({ preventScroll: true });
      });
    }
    button.setAttribute('aria-controls', smallStickerDialog.id);
    if (!smallStickerDialog.open) smallStickerDialog.showModal();
    smallStickerDialog.querySelector('h2').focus({ preventScroll: true });
  }

  function decorateSmallStickerGuide(sizes) {
    if (!config.references['1']) return;
    if (!sizes.querySelector('[data-small-sticker-guide]')) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fp-motion-size-button fp-small-sticker-trigger';
      button.dataset.smallStickerGuide = '';
      button.setAttribute('aria-haspopup', 'dialog');
      if (smallStickerDialog) button.setAttribute('aria-controls', smallStickerDialog.id);
      button.setAttribute('aria-label', '1″ Quarter — small sticker information');
      const art = document.createElement('span');
      art.className = 'fp-motion-reference';
      art.setAttribute('aria-hidden', 'true');
      art.append(image(config.references['1'], 'fp-motion-reference-image'));
      const caption = document.createElement('span');
      caption.className = 'fp-motion-reference-label';
      caption.textContent = 'Quarter';
      const info = document.createElement('span');
      info.className = 'fp-small-sticker-info';
      info.setAttribute('aria-hidden', 'true');
      info.textContent = 'i';
      button.append(art, document.createTextNode('1″'), caption, info);
      // No format/setting attributes: this button can never become an order size.
      button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        openSmallStickerGuide(button);
      });
      sizes.prepend(button);
    }
    // The catalog replaces this list when materials change. Derive the count
    // from real buttons each time, rather than incrementing its previous value.
    sizes.style.setProperty('--option-count', sizes.querySelectorAll(':scope > button').length);
  }

  function image(src, className) {
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.className = className;
    img.draggable = false;
    img.setAttribute('aria-hidden', 'true');
    return img;
  }

  function decorateMaterials() {
    root.querySelectorAll('#material-scroll [data-catalog-choice]').forEach(button => {
      const swatch = button.querySelector('.swatch');
      if (!swatch || swatch.querySelector('.fp-motion-material-mark')) return;
      // QR, bumper and floor retain their product-specific reference graphics.
      if (['qr', 'bumper', 'floor'].includes(button.dataset.catalogChoice)) return;
      swatch.append(image(config.freshF, 'fp-motion-material-mark'));
      const peel = document.createElement('span');
      peel.className = 'fp-motion-peel';
      peel.setAttribute('aria-hidden', 'true');
      swatch.append(peel);
      button.classList.add('fp-motion-material');
    });
  }

  function decorateShapes() {
    root.querySelectorAll('#shape-options [data-setting="shape"]').forEach(button => {
      if (button.querySelector('.fp-motion-shape')) return;
      if (!['die-cut', 'circle', 'square'].includes(button.dataset.value)) return;
      const art = document.createElement('span');
      art.className = `fp-motion-shape fp-motion-shape-${button.dataset.value}`;
      art.setAttribute('aria-hidden', 'true');
      art.append(image(config.freshF, 'fp-motion-shape-mark'));
      button.prepend(art);
      button.classList.add('fp-motion-shape-button');
    });
  }

  function decorateSizes() {
    const sizes = root.querySelector('#size-options');
    decorateSmallStickerGuide(sizes);
    // Standard formats are numeric. Specialty rectangular and floor dimensions
    // remain exactly as the catalog renders them, without comparison artwork.
    sizes.querySelectorAll('button').forEach(button => {
      const value = button.dataset.format || button.dataset.value;
      if (!references[value] || !config.references[value] ||
          button.querySelector('.fp-motion-reference')) return;
      const art = document.createElement('span');
      art.className = 'fp-motion-reference';
      art.setAttribute('aria-hidden', 'true');
      art.append(image(config.references[value], 'fp-motion-reference-image'));
      const caption = document.createElement('span');
      caption.className = 'fp-motion-reference-label';
      caption.textContent = references[value];
      button.prepend(art);
      button.append(caption);
      button.classList.add('fp-motion-size-button');
      if (value === '3') button.title = 'Approximate reference: a credit card is about 3.4 inches wide. This sticker is 3 inches.';
    });
    const illustrated = Boolean(sizes.querySelector('.fp-motion-size-button'));
    sizes.classList.toggle('fp-motion-sizes', illustrated);
    sizes.closest('.size-row')?.classList.toggle('fp-motion-size-row', illustrated);
    let note = root.querySelector('#fp-motion-size-note');
    if (!note) {
      note = document.createElement('p');
      note.id = 'fp-motion-size-note';
      note.className = 'fp-motion-size-note';
      note.textContent = 'Objects are approximate size references.';
      sizes.closest('.size-row')?.after(note);
    }
    note.hidden = !illustrated;
  }

  function decorate() {
    frame = 0;
    if (!root.isConnected) { observer.disconnect(); return; }
    // Disconnect while inserting decoration so our own nodes cannot create a
    // feedback loop. Observe only these three bounded control groups.
    observer.disconnect();
    decorateMaterials();
    decorateShapes();
    decorateSizes();
    groups.forEach(group => observer.observe(group, { childList: true, subtree: true }));
  }
  observer = new MutationObserver(() => {
    if (!frame) frame = requestAnimationFrame(decorate);
  });
  root.dataset.motionReady = 'true';
  decorate();

  root.addEventListener('click', event => {
    if (reducedMotion.matches || !(event.target instanceof Element)) return;
    const button = event.target.closest('#material-scroll button, #shape-options button, #size-options button');
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    const art = button.querySelector('.fp-motion-reference, .fp-motion-shape, .swatch');
    if (!art || typeof art.animate !== 'function') return;
    running.get(art)?.cancel();
    const isCard = (button.dataset.format || button.dataset.value) === '3' && button.classList.contains('fp-motion-size-button');
    const frames = isCard ? [
      { transform: 'translateX(-3px) rotate(-4deg)' },
      { transform: 'translateX(2px) rotate(2deg)', offset: 0.65 },
      { transform: 'translateX(0) rotate(0)' }
    ] : [
      { transform: 'translateY(0) rotate(0)' },
      { transform: 'translateY(-4px) rotate(-5deg)', offset: 0.4 },
      { transform: 'translateY(0) rotate(0)' }
    ];
    running.set(art, art.animate(frames, { duration: 420, easing: 'cubic-bezier(.2,.75,.25,1)' }));
  }, { passive: true });
})();
